"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useChat } from "@ai-sdk/react";
import { type ToolCall } from "@ai-sdk/provider-utils";

type ComposerContextType = {
  sendMessage: (message: string, additionalData?: Record<string, any>) => void;
  resetChat: () => void;
  messages: any[];
  status: "ready" | "streaming" | "submitted" | "error";
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSubmit: (event?: React.UIEvent, options?: any) => void;
  stop: () => void;
  setMessages: (messages: any[] | ((prev: any[]) => any[])) => void;
  setAdditionalData: (data: Record<string, any> | undefined) => void;
  additionalData: Record<string, any> | undefined;
};

const defaultContext: ComposerContextType = {
  sendMessage: () => {},
  resetChat: () => {},
  messages: [],
  status: "ready",
  input: "",
  handleInputChange: () => {},
  handleSubmit: () => {},
  stop: () => {},
  setMessages: () => {},
  setAdditionalData: () => {},
  additionalData: undefined,
};

const ComposerContext = createContext<ComposerContextType>(defaultContext);

/**
 * A provider that manages chat state and functionality
 * This allows components throughout the app to use the useComposer hook
 */
export const ComposerProvider = ({
  children,
  orchestratorHost = "canvas",
  onToolCall,
  initialAdditionalData,
}: {
  children: React.ReactNode;
  orchestratorHost?: string;
  onToolCall?: (params: { toolCall: ToolCall<string, unknown> }) => Promise<{
    toolCallId: string;
    result: any;
  }>;
  initialAdditionalData?: Record<string, any>;
}) => {
  const [chatKey, setChatKey] = useState(Date.now());
  const [additionalData, setAdditionalData] = useState<
    Record<string, any> | undefined
  >(initialAdditionalData);

  // Update additionalData when initialAdditionalData changes
  useEffect(() => {
    if (initialAdditionalData) {
      setAdditionalData(initialAdditionalData);
    }
  }, [initialAdditionalData]);

  const {
    messages,
    input,
    handleSubmit: handleSubmitChat,
    handleInputChange,
    status,
    addToolResult,
    stop,
    setMessages,
  } = useChat({
    api: "/api/zsl/chat/completions",
    maxSteps: 10,
    id: `chat-${chatKey}`,
    onFinish(args, opts) {
      if (opts.finishReason === "length") {
        console.error("The response was too long.");
      }
    },
    async onToolCall({ toolCall }) {
      if (onToolCall) {
        const result = await onToolCall({ toolCall });
        if (result) {
          addToolResult(result);
        }
      }
    },
    onError(error) {
      console.error("Error in chat:", error);
    },
  });

  const sendMessage = useCallback(
    (message: string, data?: Record<string, any>) => {
      // Set the input value to the message
      handleInputChange({
        target: { value: message },
      } as React.ChangeEvent<HTMLTextAreaElement>);

      // Submit after input change has taken effect
      setTimeout(() => {
        handleSubmitChat(undefined, {
          data: data || additionalData,
          headers: {
            "x-orchestrator-host": orchestratorHost,
          },
        });
      }, 0);
    },
    [handleInputChange, handleSubmitChat, orchestratorHost, additionalData],
  );

  const handleSubmit = useCallback(
    (event?: React.UIEvent, options?: any) => {
      handleSubmitChat(event, {
        data: options?.data || additionalData,
        headers: {
          "x-orchestrator-host": orchestratorHost,
          ...(options?.headers || {}),
        },
      });
    },
    [handleSubmitChat, orchestratorHost, additionalData],
  );

  const resetChat = useCallback(() => {
    setChatKey(Date.now());
    setAdditionalData(initialAdditionalData);
  }, [initialAdditionalData]);

  const value = {
    sendMessage,
    resetChat,
    messages,
    status,
    input,
    handleInputChange,
    handleSubmit,
    stop,
    setMessages,
    setAdditionalData,
    additionalData,
  };

  return (
    <ComposerContext.Provider value={value}>
      {children}
    </ComposerContext.Provider>
  );
};

export const useComposer = () => {
  const context = useContext(ComposerContext);
  if (!context) {
    throw new Error("useComposer must be used within a ComposerProvider");
  }
  return context;
};
